import { useTranslation } from 'react-i18next';
import { Button, Card } from '@/components/ui';
import type { ImpersonationEndReason } from '@/lib/impersonation-session';

interface ImpersonationEndedScreenProps {
  reason: ImpersonationEndReason;
}

const COPY: Record<ImpersonationEndReason, { title: string; text: string }> = {
  expired: {
    title: 'seller.impersonation.ended.expiredTitle',
    text: 'seller.impersonation.ended.expiredText',
  },
  ended: {
    title: 'seller.impersonation.ended.endedTitle',
    text: 'seller.impersonation.ended.endedText',
  },
  revoked: {
    title: 'seller.impersonation.ended.revokedTitle',
    text: 'seller.impersonation.ended.revokedText',
  },
  code_invalid: {
    title: 'seller.impersonation.ended.codeInvalidTitle',
    text: 'seller.impersonation.ended.codeInvalidText',
  },
};

/**
 * The only screen an impersonation tab shows once its session is over (or
 * never started). Deliberately without links into the app: every route in
 * this tab would run without a session, and /login would sign someone in
 * inside a tab that must stay apart from the admin's session. The way on
 * is closing the tab and starting the jump again in the admin tab.
 */
export function ImpersonationEndedScreen({
  reason,
}: ImpersonationEndedScreenProps) {
  const { t } = useTranslation();
  const copy = COPY[reason];

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6 py-10">
      <Card
        variant="section"
        padding="none"
        role="alert"
        data-reason={reason}
        className="flex w-full max-w-[520px] flex-col items-center gap-4 px-10 py-11 text-center"
      >
        <p className="dc-eyebrow">{t('seller.impersonation.ended.eyebrow')}</p>
        <h1 className="dc-h1 text-[30px] text-ink">{t(copy.title)}</h1>
        <p className="text-[15px] text-ink-2">{t(copy.text)}</p>
        <p className="text-[15px] text-ink-2">
          {t('seller.impersonation.ended.closeHint')}{' '}
          {t('seller.impersonation.ended.restartHint')}
        </p>
        <p className="max-w-[420px] rounded-chip bg-paper px-3 py-2 text-[11.5px] font-semibold text-warning ring-1 ring-inset ring-warning">
          {t('seller.impersonation.ended.unsavedHint')}
        </p>
        <Button type="button" onClick={() => window.close()}>
          {t('seller.impersonation.ended.closeTab')}
        </Button>
      </Card>
    </div>
  );
}
